import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useHistory } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  BusinessOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  EditOutlined,
  HomeWorkOutlined,
  KeyboardArrowDown,
  LocationCityOutlined,
  MapOutlined,
  MyLocationOutlined,
  PinDropOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import { Loader } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SiteLocationMap } from "components/DesignSystem/SiteLocationMap";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette, detailCardSx } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { showToast } from "services/utils/Status";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { RecordHeaderCard, SwitchLine, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import {
  BODY_TYPES,
  GEOFENCE_RADII,
  INDIAN_STATES,
  SETUP_ROUTES,
  SITE_TYPES,
  emptySiteForm,
  formChanged,
  formToApi,
  formatDay,
  joinContact,
  parseLatLong,
  siteToForm,
  siteTypeLabel,
  splitServerErrors,
  validateSiteForm,
} from "./sitesData";
import {
  createDistrict,
  createLocalBody,
  createSite,
  getDistricts,
  getLocalBodies,
  getNextSiteCode,
  lookupPincode,
  updateSite,
} from "./sitesApi";

const { indigo, pink, blue, green, orange, purple } = detailAccentPalette;
const RED = "#DC2626";
const ORANGE = "#F97316";
const ACTIVE_COLORS = { Active: "#12B76A", Inactive: "#667085" };
const STATE_OPTIONS = INDIAN_STATES.map((state) => ({ value: state, label: state }));
const NO_COMPANY_NOTE =
  "No company is chosen here. A site can host several companies — they come from the employees who work at the site, set in Employee Onboarding or Employee Edit.";

// ---------------- Creatable select (district / local body) ----------------

// Options [{ id, name }]. Typing a new name offers "+ Add 'X'"; picking it
// calls onCreate(name). The server returns the existing row for a name it
// already has (any case), so a duplicate just selects that row. Built-in
// districts not saved yet have id null: picking one also calls onCreate.
export const creatableOptions = (list, inputValue) => {
  const typed = String(inputValue || "").trim();
  const needle = typed.toLowerCase();
  const filtered = list.filter((option) => option.name.toLowerCase().includes(needle));
  const exists = list.some((option) => option.name.toLowerCase() === needle);
  return typed && !exists ? [...filtered, { id: `new:${typed}`, create: typed, name: typed }] : filtered;
};

export const CreatableSelect = ({ id, value, options, onChange, onCreate, disabled, placeholder, error, loading, label }) => {
  const [input, setInput] = useState("");
  return (
    <Mui.Autocomplete
      id={id}
      size="small"
      disabled={disabled}
      loading={loading}
      loadingText="Loading..."
      // Nothing saved yet: say how to add one instead of a bare "No options".
      noOptionsText="None saved yet. Type a name to add it."
      options={options}
      value={value}
      inputValue={input}
      onInputChange={(event, next) => setInput(next)}
      isOptionEqualToValue={(option, selected) =>
        option.id != null && selected.id != null ? option.id === selected.id : option.name.toLowerCase() === selected.name.toLowerCase()
      }
      getOptionLabel={(option) => (option.create ? `+ Add "${option.create}"` : option.name || "")}
      filterOptions={(list, state) => creatableOptions(list, state.inputValue)}
      onChange={(event, option) => {
        if (option?.create) onCreate(option.create);
        else if (option && option.id == null) onCreate(option.name);
        else onChange(option || null);
      }}
      renderInput={(params) => (
        <Mui.TextField {...params} placeholder={placeholder} error={Boolean(error)} inputProps={{ ...params.inputProps, "aria-label": label }} />
      )}
    />
  );
};

const DateInput = ({ value, onChange, error }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={value ? dayjs(value) : null}
      onChange={(next) => onChange(next && next.isValid() ? next.format("YYYY-MM-DD") : "")}
      format="DD MMM YYYY"
      slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "01 Nov 2026", error: Boolean(error) } }}
      sx={{ width: "100%" }}
    />
  </LocalizationProvider>
);

const TypeChips = ({ value, onChange }) => (
  <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" role="radiogroup" aria-label="Type">
    {SITE_TYPES.map((type) => {
      const selected = value === type.value;
      return (
        <Mui.Chip
          key={type.value}
          role="radio"
          aria-checked={selected}
          label={type.label}
          onClick={() => onChange(type.value)}
          variant={selected ? "filled" : "outlined"}
          sx={{
            borderRadius: "8px",
            fontWeight: selected ? 600 : 500,
            backgroundColor: selected ? designColors.tealTint : designColors.white,
            color: selected ? designColors.tealDark : designColors.slate700,
            borderColor: selected ? designColors.tealDark : designColors.slate200,
            border: "1px solid",
          }}
        />
      );
    })}
  </Mui.Stack>
);

// ---------------- Add / Edit Site ----------------

export const AddEditSiteDrawer = ({ open, mode = "add", site, domain, onClose, onSaved }) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(emptySiteForm);
  const [initial, setInitial] = useState(emptySiteForm);
  const [showErrors, setShowErrors] = useState(false);
  const [server, setServer] = useState({ fields: {}, general: "" });
  const [saving, setSaving] = useState(false);
  const [districts, setDistricts] = useState({ loading: false, rows: [] });
  const [localBodies, setLocalBodies] = useState({ loading: false, rows: [] });
  const [newLocalBody, setNewLocalBody] = useState(null); // { name, bodyType }
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Fields the user picked themselves: the PIN lookup leaves them alone.
  const touched = useRef(new Set());
  const codeTyped = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const start = isEdit && site ? siteToForm(site) : emptySiteForm();
    setForm(start);
    setInitial(start);
    setShowErrors(false);
    setServer({ fields: {}, general: "" });
    setSaving(false);
    setNewLocalBody(null);
    setConfirmDiscard(false);
    touched.current = new Set();
    codeTyped.current = false;
    if (isEdit) return undefined;
    // Add: suggest the next code, unless the user already typed one.
    const controller = new AbortController();
    getNextSiteCode({ domain, signal: controller.signal })
      .then((code) => {
        if (codeTyped.current || !code) return;
        setForm((current) => ({ ...current, code }));
        setInitial((current) => ({ ...current, code }));
      })
      .catch(() => null);
    return () => controller.abort();
  }, [open, isEdit, site, domain]);

  useEffect(() => {
    if (!open || !form.state) {
      setDistricts({ loading: false, rows: [] });
      return undefined;
    }
    const controller = new AbortController();
    setDistricts((current) => ({ ...current, loading: true }));
    getDistricts({ domain, state: form.state, signal: controller.signal })
      .then((rows) => setDistricts({ loading: false, rows }))
      .catch(() => !controller.signal.aborted && setDistricts({ loading: false, rows: [] }));
    return () => controller.abort();
  }, [open, domain, form.state]);

  const districtId = form.district?.id;
  useEffect(() => {
    if (!open || !districtId) {
      setLocalBodies({ loading: false, rows: [] });
      return undefined;
    }
    const controller = new AbortController();
    setLocalBodies((current) => ({ ...current, loading: true }));
    getLocalBodies({ domain, districtId, signal: controller.signal })
      .then((rows) => setLocalBodies({ loading: false, rows }))
      .catch(() => !controller.signal.aborted && setLocalBodies({ loading: false, rows: [] }));
    return () => controller.abort();
  }, [open, domain, districtId]);

  const clearServer = (key) => setServer((current) => ({ ...current, fields: { ...current.fields, [key]: undefined } }));
  const set = (key) => (value) => {
    if (key === "code") codeTyped.current = true;
    setForm((current) => ({ ...current, [key]: value }));
    clearServer(key);
  };
  // Changing State clears District and Local body; District clears Local body.
  const setState = (state) => {
    touched.current.add("state");
    setForm((current) => (current.state === state ? current : { ...current, state, district: null, local_body: null }));
    clearServer("state");
  };
  const setDistrict = (district) => {
    touched.current.add("district");
    setForm((current) => (current.district?.id === district?.id ? current : { ...current, district, local_body: null }));
    setNewLocalBody(null);
    clearServer("district");
  };
  const setLocalBody = (localBody) => {
    touched.current.add("local_body");
    setForm((current) => ({ ...current, local_body: localBody }));
    clearServer("local_body");
  };

  // A known PIN fills State / District / Local body the user hasn't set.
  const setPincode = async (raw) => {
    const pincode = raw.replace(/\D/g, "").slice(0, 6);
    set("pincode")(pincode);
    if (pincode.length !== 6) return;
    try {
      const found = await lookupPincode({ domain, pin: pincode });
      if (!found?.state) return;
      setForm((current) => {
        if (current.pincode !== pincode) return current;
        const next = { ...current };
        const sameState = !touched.current.has("state") || current.state === found.state;
        if (!touched.current.has("state")) next.state = found.state;
        if (sameState && !touched.current.has("district")) next.district = found.district;
        if (sameState && !touched.current.has("district") && !touched.current.has("local_body")) next.local_body = found.local_body;
        return next;
      });
    } catch (error) {
      // No lookup: the user fills the fields.
    }
  };

  const addDistrict = async (name) => {
    try {
      const district = await createDistrict({ domain, state: form.state, name });
      // The saved row replaces the built-in name it came from.
      setDistricts((current) => ({
        ...current,
        rows: [
          ...current.rows.filter((row) => row.id !== district.id && row.name.toLowerCase() !== district.name.toLowerCase()),
          district,
        ].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setDistrict(district);
    } catch (error) {
      showToast("error", error.message || "Could not add the district.");
    }
  };

  const addLocalBody = async () => {
    if (!newLocalBody?.bodyType) return;
    try {
      const localBody = await createLocalBody({ domain, districtId, name: newLocalBody.name, bodyType: newLocalBody.bodyType });
      setLocalBodies((current) => ({
        ...current,
        rows: current.rows.some((row) => row.id === localBody.id) ? current.rows : [...current.rows, localBody],
      }));
      setLocalBody(localBody);
      setNewLocalBody(null);
    } catch (error) {
      showToast("error", error.message || "Could not add the local body.");
    }
  };

  const fillCurrentLocation = () => {
    if (!navigator?.geolocation) {
      showToast("error", "Location is not available in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => set("location")(`${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`),
      () => showToast("error", "Could not get your location."),
    );
  };

  const errors = validateSiteForm(form);
  const parsedPoint = parseLatLong(form.location);
  const mapPoint = parsedPoint && !parsedPoint.error ? parsedPoint : null;
  const error = (key) => server.fields[key] || (showErrors ? errors[key] : undefined);
  const dirty = formChanged(initial, form);

  const cancel = () => {
    if (dirty && !saving) setConfirmDiscard(true);
    else onClose();
  };

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length || saving) return;
    setSaving(true);
    setServer({ fields: {}, general: "" });
    try {
      const body = formToApi(form);
      const result = isEdit ? await updateSite({ domain, id: site.id, body }) : await createSite({ domain, body });
      onSaved(result.site, result.message);
    } catch (saveError) {
      setServer(splitServerErrors(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={cancel}
        title={isEdit ? "Edit Site" : "Add Site"}
        subtitle={isEdit ? `${site?.name || ""} · ${site?.code || ""}` : "Where employees work. Location decides Professional Tax and LWF."}
        bodySx={panelBodySx}
        footer={<PanelActionFooter onCancel={cancel} primaryLabel={saving ? "Saving..." : "Save Site"} primaryProps={{ onClick: save, disabled: saving }} />}
      >
        <Mui.Stack spacing={2}>
          {server.general ? <RequestInfoBanner tone="danger" title="Couldn't save" body={server.general} /> : null}

          <FormSection title="Site details">
            <Field label={<>Site name<Required /></>} helper="Shown in lists and reports." error={error("name")}>
              <TextField value={form.name} onChange={set("name")} placeholder="e.g. Erode Unit 1" error={error("name")} />
            </Field>
            <Field label={<>Site code<Required /></>} helper="Unique. Used in imports." error={error("code")}>
              <TextField value={form.code} onChange={set("code")} placeholder="SITE-01" error={error("code")} />
            </Field>
            <Field label={<>Type<Required /></>} helper="Kind of workplace." error={error("site_type")} wide>
              <TypeChips value={form.site_type} onChange={set("site_type")} />
            </Field>
            <Field label={<>Active from<Required /></>} helper="First day the site is used." error={error("active_from")}>
              <DateInput value={form.active_from} onChange={set("active_from")} error={error("active_from")} />
            </Field>
            <Field label="Status" helper="Turn off when not in use.">
              <SwitchLine checked={form.is_active} onChange={set("is_active")} label={form.is_active ? "Active" : "Inactive"} />
            </Field>
          </FormSection>

          <FormSection title="Location" info="Decides Professional Tax and LWF">
            <Field label={<>Address line 1<Required /></>} helper="Door no and street." error={error("address_line1")} wide>
              <TextField value={form.address_line1} onChange={set("address_line1")} placeholder="Door no, street" error={error("address_line1")} />
            </Field>
            <Field label="Address line 2" helper="Area or landmark (optional)." error={error("address_line2")} wide>
              <TextField value={form.address_line2} onChange={set("address_line2")} placeholder="Area, landmark (optional)" />
            </Field>
            <Field label={<>PIN code<Required /></>} helper="Fills state and district." error={error("pincode")}>
              <Mui.TextField
                size="small"
                fullWidth
                value={form.pincode}
                onChange={(event) => setPincode(event.target.value)}
                placeholder="638011"
                error={Boolean(error("pincode"))}
                inputProps={{ inputMode: "numeric", maxLength: 6, "aria-label": "PIN code" }}
              />
            </Field>
            <Field label={<>State<Required /></>} helper="State where the site is." error={error("state")}>
              <SelectField value={form.state} onChange={setState} options={STATE_OPTIONS} placeholder="Select state" error={error("state")} />
            </Field>
            <Field
              label={<>District<Required /></>}
              helper={form.state ? "Pick from list or add new." : "Pick a state first."}
              error={error("district")}
            >
              <CreatableSelect
                id="site-district"
                label="District"
                value={form.district}
                options={districts.rows}
                loading={districts.loading}
                disabled={!form.state}
                placeholder={form.state ? "Select or add district" : "Pick a state first"}
                onChange={setDistrict}
                onCreate={addDistrict}
                error={error("district")}
              />
            </Field>
            <Field label={<>City / Local body<Required /></>} helper="Municipality or panchayat." error={error("local_body")}>
              <CreatableSelect
                id="site-local-body"
                label="City / Local body"
                value={form.local_body}
                options={localBodies.rows}
                loading={localBodies.loading}
                disabled={!form.district}
                placeholder={form.district ? "Select or add local body" : "Pick a district first"}
                onChange={setLocalBody}
                onCreate={(name) => setNewLocalBody({ name, bodyType: "" })}
                error={error("local_body")}
              />
            </Field>
            {newLocalBody ? (
              <Mui.Box sx={{ gridColumn: "1 / -1", ...detailCardSx, backgroundColor: designColors.pageBg }}>
                <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, mb: 1 }}>
                  New local body: {newLocalBody.name}
                </Mui.Typography>
                <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                  <Mui.Box sx={{ flex: 1 }}>
                    <SelectField
                      value={newLocalBody.bodyType}
                      onChange={(bodyType) => setNewLocalBody((current) => ({ ...current, bodyType }))}
                      options={BODY_TYPES}
                      placeholder="Pick the body type"
                    />
                  </Mui.Box>
                  <SecondaryButton onClick={() => setNewLocalBody(null)}>Cancel</SecondaryButton>
                  <PrimaryButton onClick={addLocalBody} disabled={!newLocalBody.bodyType}>
                    Add
                  </PrimaryButton>
                </Mui.Stack>
              </Mui.Box>
            ) : null}
          </FormSection>

          <FormSection title="Attendance (Optional)" info="Stored for geofenced attendance.">
            <Field label="Latitude, longitude" helper="Site location on the map." error={error("location")}>
              <Mui.TextField
                size="small"
                fullWidth
                value={form.location}
                onChange={(event) => set("location")(event.target.value)}
                placeholder="11.341036, 77.717163"
                error={Boolean(error("location"))}
                inputProps={{ "aria-label": "Latitude, longitude" }}
                InputProps={{
                  endAdornment: (
                    <Mui.InputAdornment position="end">
                      <Mui.Tooltip title="Use my current location" arrow>
                        <Mui.IconButton size="small" onClick={fillCurrentLocation} aria-label="Use my current location">
                          <MyLocationOutlined fontSize="small" />
                        </Mui.IconButton>
                      </Mui.Tooltip>
                    </Mui.InputAdornment>
                  ),
                }}
              />
            </Field>
            <Field label="Geofence radius" helper="Allowed punch distance." error={error("geofence_radius_m")}>
              <SelectField
                value={form.geofence_radius_m}
                onChange={set("geofence_radius_m")}
                options={GEOFENCE_RADII}
                placeholder="Select radius"
                error={error("geofence_radius_m")}
              />
            </Field>
            <Field label="Site contact" helper="Person to reach at the site, with phone number." error={error("contact")} wide>
              <TextField value={form.contact} onChange={set("contact")} placeholder="e.g. Karthik R · 98430 12345" error={error("contact")} />
            </Field>
            <Field label="Location on Map" helper="Pin and geofence from the values above." wide>
              <SiteLocationMap
                latitude={mapPoint?.latitude}
                longitude={mapPoint?.longitude}
                radius={form.geofence_radius_m}
                label={form.local_body?.name || form.name}
                emptyText="Enter latitude, longitude to see the site on the map."
              />
            </Field>
          </FormSection>

          <RequestInfoBanner body={NO_COMPANY_NOTE} />
        </Mui.Stack>
      </GlobalSidePanel>
      <GlobalConfirmDialog
        open={confirmDiscard}
        title="Discard changes?"
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        danger
      >
        Your changes to this site will be lost.
      </GlobalConfirmDialog>
    </>
  );
};

// ---------------- Site Details ----------------

const SectionCard = ({ title, note, children }) => (
  <Mui.Box sx={detailCardSx}>
    <Mui.Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
      <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
      {note ? <Mui.Typography sx={designType.captionText}>{note}</Mui.Typography> : null}
    </Mui.Stack>
    {children}
  </Mui.Box>
);

export const CompaniesAtSite = ({ companies }) =>
  companies?.length ? (
    <Mui.Stack spacing={1}>
      {companies.map((company) => (
        <Mui.Stack key={company.id} direction="row" spacing={1.25} alignItems="center">
          <Mui.Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
            {initialsOf(company.name)}
          </Mui.Avatar>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, flex: 1, minWidth: 0 }}>{company.name}</Mui.Typography>
          <ToneChip label={company.role === "PRIMARY" ? "Primary" : "Secondary"} tone={company.role === "PRIMARY" ? "teal" : "slate"} />
          <Mui.Typography sx={{ ...designType.captionText, whiteSpace: "nowrap" }}>
            {Number(company.employee_count).toLocaleString("en-IN")} employees
          </Mui.Typography>
        </Mui.Stack>
      ))}
    </Mui.Stack>
  ) : (
    <Mui.Typography sx={designType.helperText}>No employees assigned to this site yet</Mui.Typography>
  );

const coverageTitle = (row) => (row.company ? `${row.type} · ${row.company.name}` : row.type);

// Set up, or decided not to apply (LWF rows: Not applicable / Exempt) —
// neither needs a "Set up" link.
const COVERAGE_CHIPS = {
  MAPPED: { label: "Mapped", tone: "green" },
  COVERED: { label: "Covered", tone: "green" },
  NOT_APPLICABLE: { label: "Not applicable", tone: "slate" },
  EXEMPT: { label: "Exempt", tone: "slate" },
};

// "Tamil Nadu · ₹20 + ₹40 · Yearly (Dec) · Version 1 · Reg. No. TN/LWF/0045821"
const coverageDetail = (row) => {
  const version = typeof row.version === "object" ? row.version?.name : row.version;
  return [row.label, row.value, version, row.registration_no ? `Reg. No. ${row.registration_no}` : ""].filter(Boolean).join(" · ");
};

export const StatutoryCoverage = ({ rows, onSetUp }) => (
  <Mui.Stack divider={<Mui.Divider flexItem />} spacing={1}>
    {(rows || []).map((row, index) => {
      const chip = COVERAGE_CHIPS[String(row.status || "").toUpperCase()];
      const set = Boolean(chip);
      return (
        <Mui.Stack key={`${row.type}-${row.company?.id ?? index}`} direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{coverageTitle(row)}</Mui.Typography>
            {set ? (
              <Mui.Typography sx={designType.captionText}>{coverageDetail(row)}</Mui.Typography>
            ) : (
              <Mui.Typography sx={{ ...designType.captionText, color: ORANGE }}>
                {row.label ? `${row.label} · ` : ""}No {row.type} code covers this site
              </Mui.Typography>
            )}
          </Mui.Box>
          {set ? (
            <ToneChip label={chip.label} tone={chip.tone} />
          ) : (
            <Mui.Link component="button" type="button" onClick={() => onSetUp(row.type)} sx={{ ...designType.tableBodyCell, fontWeight: 600, whiteSpace: "nowrap" }}>
              Set up
            </Mui.Link>
          )}
        </Mui.Stack>
      );
    })}
  </Mui.Stack>
);

export const SiteDetailsDrawer = ({ open, loading, error, site, canManage, onRetry, onClose, onEdit, onAction }) => {
  const history = useHistory();
  const [menuAnchor, setMenuAnchor] = useState(null);
  if (!open) return null;

  const pick = (type) => {
    setMenuAnchor(null);
    onAction(type, site);
  };

  const footer =
    site && canManage ? (
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
        <SecondaryButton endIcon={<KeyboardArrowDown />} onClick={(event) => setMenuAnchor(event.currentTarget)}>
          More Actions
        </SecondaryButton>
        <Mui.Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {!site.is_active ? <Mui.MenuItem onClick={() => pick("activate")}>Activate</Mui.MenuItem> : null}
          <Mui.MenuItem onClick={() => pick("move")}>Move employees</Mui.MenuItem>
          <Mui.MenuItem onClick={() => pick("delete")} sx={{ color: RED }}>
            Delete
          </Mui.MenuItem>
        </Mui.Menu>
        {site.is_active ? (
          <PrimaryButton onClick={() => pick("deactivate")} sx={{ backgroundColor: RED, "&:hover": { backgroundColor: "#B91C1C" } }}>
            Deactivate Site
          </PrimaryButton>
        ) : null}
      </Mui.Stack>
    ) : (
      <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
        <PrimaryButton onClick={onClose}>Close</PrimaryButton>
      </Mui.Stack>
    );

  let body;
  if (error && !site) {
    body = (
      <RequestInfoBanner
        tone="danger"
        title="Couldn't load the site."
        body={error}
        action={<SecondaryButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
      />
    );
  } else if (loading || !site) {
    body = (
      <Mui.Box role="status" aria-label="Loading site">
        <Loader info="Loading..." minHeight="40vh" />
      </Mui.Box>
    );
  } else {
    const status = site.is_active ? "Active" : "Inactive";
    const contact = joinContact(site.contact_name, site.contact_phone);
    const hasAttendance = site.latitude != null || contact;
    body = (
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={<PlaceOutlined />}
          title={site.name}
          chip={<RequestStatusChip status={status} colorCode={ACTIVE_COLORS[status]} />}
          meta={`${site.code} · ${siteTypeLabel(site.site_type)} · ${Number(site.employee_count || 0).toLocaleString("en-IN")} employees`}
          tone={site.is_active ? "teal" : "slate"}
        />

        <RequestDetailsSection
          title="LOCATION"
          fields={[
            { icon: HomeWorkOutlined, label: "Address", value: [site.address_line1, site.address_line2].filter(Boolean).join(", ") || "—", accent: indigo, multiline: true },
            { icon: PinDropOutlined, label: "PIN Code", value: site.pincode || "—", accent: green },
            { icon: LocationCityOutlined, label: "City / Local Body", value: site.local_body?.name || "—", accent: blue },
            { icon: MapOutlined, label: "District · State", value: [site.district?.name, site.state].filter(Boolean).join(" · ") || "—", accent: purple },
            { icon: CategoryOutlined, label: "Type", value: siteTypeLabel(site.site_type), accent: orange },
            { icon: CalendarMonthOutlined, label: "Active from", value: formatDay(site.active_from), accent: pink },
          ]}
        />

        {hasAttendance ? (
          <RequestDetailsSection
            title="ATTENDANCE"
            fields={[
              ...(site.latitude != null
                ? [{ icon: MyLocationOutlined, label: "Latitude, longitude", value: `${site.latitude}, ${site.longitude}` + (site.geofence_radius_m ? ` · ${site.geofence_radius_m} m` : ""), accent: blue }]
                : []),
              ...(contact ? [{ icon: BusinessOutlined, label: "Site contact", value: contact, accent: indigo }] : []),
            ]}
          />
        ) : null}

        {site.latitude != null && site.longitude != null ? (
          <SectionCard title="Location on Map">
            <SiteLocationMap
              latitude={site.latitude}
              longitude={site.longitude}
              radius={site.geofence_radius_m}
              label={site.local_body?.name || site.name}
            />
          </SectionCard>
        ) : null}

        <SectionCard title="Companies at this site" note="From employees working here">
          <CompaniesAtSite companies={site.companies} />
        </SectionCard>

        <SectionCard title="Statutory coverage" note="Set in Payroll › Configuration › Statutory rules">
          <StatutoryCoverage rows={site.statutory_coverage} onSetUp={(type) => SETUP_ROUTES[type] && history?.push(SETUP_ROUTES[type])} />
        </SectionCard>
      </Mui.Stack>
    );
  }

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Site Details"
      subtitle="Location, companies and statutory coverage"
      bodySx={panelBodySx}
      footer={footer}
      headerActions={
        site && canManage ? (
          <Mui.Tooltip title="Edit" arrow>
            <Mui.IconButton aria-label="Edit site" onClick={() => onEdit(site)}>
              <EditOutlined fontSize="small" />
            </Mui.IconButton>
          </Mui.Tooltip>
        ) : null
      }
    >
      {body}
    </GlobalSidePanel>
  );
};
